import { escapeCsvCell } from 'src/admin/LAB/PoolManagement/csvEscape';
import type { InventoryRow } from 'src/admin/models/PoolInventoryModel';

/**
 * Column order for the pool export, mirroring the on-screen inventory
 * table (see `InventoryTable.svelte`) so the download matches what the
 * user sees. This is intentionally wider than {@link INVENTORY_CSV_HEADER}:
 * the export is a snapshot/report, not a re-upload source, so it carries
 * read-only columns (borrow status, prices, revenue) the parser ignores.
 *
 * Column labels track the table headers (PLAT-13313), not the API
 * fields: "Clearing Borrow Status" is the Wedbush `borrowStatus`,
 * "Remaining Quantity" is `currentQuantity`, "Admin Price" is
 * `currentLabPrice` and "Reference Price" is `currentPrice`. Behind the
 * `enable-lab-firm-borrow-status` flag the table adds a "System Borrow
 * Status" column (`firmBorrowStatus`) after the clearing one and the
 * export follows suit.
 */
const POOL_EXPORT_CSV_HEADER =
    'Symbol,Clearing Borrow Status,Quantity Used,Remaining Quantity,Admin Price,Reference Price,Fair Value,Daily Revenue';
const POOL_EXPORT_CSV_HEADER_WITH_FIRM_STATUS =
    'Symbol,Clearing Borrow Status,System Borrow Status,Quantity Used,Remaining Quantity,Admin Price,Reference Price,Fair Value,Daily Revenue';

/** The inventory-row fields the pool export serializes, in table order. */
type PoolExportRow = Pick<
    InventoryRow,
    | 'symbol'
    | 'borrowStatus'
    | 'firmBorrowStatus'
    | 'quantityUsed'
    | 'currentQuantity'
    | 'currentLabPrice'
    | 'currentPrice'
    | 'fairValuePrice'
    | 'dailyRevenue'
>;

/**
 * Serialize the current pool inventory to CSV, one column per visible
 * table column and in the same order (see {@link POOL_EXPORT_CSV_HEADER}).
 * `includeFirmBorrowStatus` matches the table's flag gate: when set, the
 * system borrow status is exported after the clearing one.
 * Nullable columns (borrow statuses, reference/fair-value prices, quantity
 * used, daily revenue) serialize to an empty cell — the table renders
 * these as an em dash, but a blank is the machine-readable equivalent.
 * Cells are escaped per RFC 4180 for `,` and `"`; embedded CR/LF are
 * normalized to spaces by {@link escapeCsvCell}.
 */
export function createInventoryCsv(rows: ReadonlyArray<PoolExportRow>, includeFirmBorrowStatus = false): string {
    const lines = [includeFirmBorrowStatus ? POOL_EXPORT_CSV_HEADER_WITH_FIRM_STATUS : POOL_EXPORT_CSV_HEADER];
    for (const row of rows) {
        lines.push(
            [
                row.symbol,
                row.borrowStatus ?? '',
                ...(includeFirmBorrowStatus ? [row.firmBorrowStatus ?? ''] : []),
                row.quantityUsed ?? '',
                row.currentQuantity,
                row.currentLabPrice,
                row.currentPrice ?? '',
                row.fairValuePrice ?? '',
                row.dailyRevenue ?? '',
            ]
                .map(escapeCsvCell)
                .join(','),
        );
    }
    return lines.join('\n') + '\n';
}
